import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Globe, Calendar, Heart } from 'lucide-react';

const CommunityResources = () => {
  const navigate = useNavigate();

  const communities = [
    {
      title: "Expat Organizations",
      description: "Connect with international communities",
      items: [
        {
          name: "InterNations Poland",
          description: "Large expat network organizing regular events and activities in major Polish cities",
          link: "https://www.internations.org/poland-expats"
        },
        {
          name: "Expats in Poland",
          description: "Facebook community for expats living in Poland",
          link: "https://www.facebook.com/groups/expatsinpoland"
        },
        {
          name: "Meetup Groups",
          description: "Various international and language exchange groups",
          link: "https://www.meetup.com/topics/expat/pl/"
        }
      ]
    },
    {
      title: "Language & Cultural Exchange",
      description: "Improve your Polish and meet locals",
      items: [
        {
          name: "Language Exchange Events",
          description: "Regular meetups for practicing Polish and other languages"
        },
        {
          name: "Cultural Integration Programs",
          description: "Programs helping expats understand Polish culture and customs"
        },
        {
          name: "International Cultural Centers",
          description: "Centers organizing cultural events and workshops"
        }
      ]
    },
    {
      title: "Support Services",
      description: "Practical help for daily life",
      items: [
        {
          name: "Expat Center",
          description: "Assistance with administrative procedures and daily life matters"
        },
        {
          name: "International Schools Network",
          description: "Community of international school parents and staff"
        },
        {
          name: "Healthcare Support Groups",
          description: "Information about healthcare services in English"
        }
      ]
    },
    {
      title: "Professional Networks",
      description: "Career and business connections",
      items: [
        {
          name: "International Business Associations",
          description: "Network with professionals and entrepreneurs"
        },
        {
          name: "Industry-Specific Groups",
          description: "Connect with others in your professional field"
        },
        {
          name: "Startup Communities",
          description: "Meet other international entrepreneurs"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Users className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Community Resources
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Connect with expat communities and support groups in Poland
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {communities.map((community, index) => (
            <Card key={index} className="hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-xl text-foreground">{community.title}</CardTitle>
                <CardDescription>{community.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  {community.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="space-y-1">
                      <h3 className="font-medium text-foreground">{item.name}</h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:text-primary/80 transition-colors"
                        >
                          Visit Website →
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 bg-card rounded-lg p-6 border border-border">
          <h2 className="text-2xl font-bold text-foreground mb-4">Getting Involved in the Community</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <Globe className="h-5 w-5 text-primary mt-1" />
              <p className="text-muted-foreground">
                Join online communities to connect with other expats before and after your move
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="h-5 w-5 text-primary mt-1" />
              <p className="text-muted-foreground">
                Attend regular meetups and events to build your social network
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <Heart className="h-5 w-5 text-primary mt-1" />
              <p className="text-muted-foreground">
                Volunteer in local community projects to integrate with Polish society
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/knowledge-base/support-resources')}
            className="text-primary hover:text-primary/80 transition-colors"
          >
            ← Back to Support Resources
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunityResources; 